// The claim surface, and the one place in this app that sends a CSRF token.
//
// Every other call the portal makes carries a bearer token in a header, which
// is why those endpoints need no CSRF defence: a cross-site page cannot make
// the browser attach a header. The claim is the exception, and it has to be —
// the person arriving from a marketplace hand-off has no token, and often no
// account. Their credential is the server-side session the hand-off wrote, and
// a session cookie IS something the browser attaches by itself. So these three
// addresses are ordinary CSRF-protected posts, and this module reads the
// `csrftoken` cookie the first GET hands out and sends it back in
// `X-CSRFToken`. Reading that cookie moved to ../auth/csrf when the federated
// sign-in became the second surface that needs it.
//
// Nothing here holds the claimed session. The successful claim returns an
// access token in the same shape a sign-in does, and the caller hands it to the
// session provider — which is where a token lives, in memory, and nowhere else.

import { csrfToken } from '../auth/csrf';
import type { Customer, Subscription } from '../data/subscriptions';

const BASE = '/portal/claim';

/** What the hand-off left waiting, and what claiming it would bind them to. */
export interface WaitingClaim {
  subscription: Subscription;
  customer: Customer;

  /**
   * Whether nobody has told us what this company is called yet.
   *
   * The server decides this, and the journey must not try to work it out from
   * `customer.display_name`. The placeholder is whatever the selling channel's
   * adapter could honestly say about the buyer — it differs per channel and
   * will differ again for the next one — so any client-side test of that
   * string is a test that silently stops recognising a placeholder the day a
   * second channel goes live.
   *
   * False means the company has a name somebody chose. The journey states it
   * rather than offering to change it: the backend's `name_the_company` would
   * ignore the answer, and a form whose answer is discarded is worse than no
   * form.
   */
  company_name_is_a_placeholder: boolean;
}

/** A completed claim: a session, and what it was for. */
export interface ClaimedSubscription {
  accessToken: string;
  expiresInSeconds: number;
  subscription: Subscription;
  customer: Customer;
}

/** The service refused, and this is what it said. */
export class ClaimRefused extends Error {
  readonly status: number;

  /** Per-field problems where an account could not be made from what was given. */
  readonly problems: Record<string, string[]>;

  /**
   * True when the address already holds an account.
   *
   * Not a correction the person can make to this form — it means the other
   * door. The journey offers it rather than printing a sentence by an input.
   */
  readonly alreadyRegistered: boolean;

  constructor(
    status: number,
    message: string,
    problems: Record<string, string[]> = {},
    alreadyRegistered = false,
  ) {
    super(message);
    this.name = 'ClaimRefused';
    this.status = status;
    this.problems = problems;
    this.alreadyRegistered = alreadyRegistered;
  }
}

/**
 * Ask what this browser's session is holding.
 *
 * A 404 is the ordinary answer for somebody who did not arrive from a channel,
 * and it is not an error condition — the journey has nothing to show and says
 * so. The caller distinguishes the two, not this function.
 */
export async function fetchWaitingClaim(): Promise<WaitingClaim> {
  const response = await fetch(`${BASE}/waiting/`, {
    method: 'GET',
    credentials: 'same-origin',
  });
  return read<WaitingClaim>(response);
}

/**
 * Make an account, take hold of what is waiting, and come back signed in.
 *
 * `companyName` is sent by both doors and is optional at both, because which
 * door somebody comes through says nothing about whether their company has
 * been named. Empty means they said nothing, which the server reads as "leave
 * the name alone" rather than as "call it nothing".
 */
export async function claimWithNewAccount(details: {
  email: string;
  password: string;
  name: string;
  companyName: string;
  keepSignedIn: boolean;
}): Promise<ClaimedSubscription> {
  return claimed(
    await post(`${BASE}/account/`, {
      email: details.email,
      password: details.password,
      name: details.name,
      company_name: details.companyName,
      keep_signed_in: details.keepSignedIn,
    }),
  );
}

/** Sign in to the account they already hold, then take hold of what is waiting. */
export async function claimWithExistingAccount(details: {
  email: string;
  password: string;
  companyName: string;
  keepSignedIn: boolean;
}): Promise<ClaimedSubscription> {
  return claimed(
    await post(`${BASE}/sign-in/`, {
      email: details.email,
      password: details.password,
      company_name: details.companyName,
      keep_signed_in: details.keepSignedIn,
    }),
  );
}

async function post(path: string, payload: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken(),
    },
    body: JSON.stringify(payload),
  });
}

async function claimed(response: Response): Promise<ClaimedSubscription> {
  const body = await read<{
    access_token: string;
    expires_in: number;
    subscription: Subscription;
    customer: Customer;
  }>(response);

  return {
    accessToken: body.access_token,
    expiresInSeconds: body.expires_in,
    subscription: body.subscription,
    customer: body.customer,
  };
}

async function read<T>(response: Response): Promise<T> {
  if (response.ok) {
    return (await response.json()) as T;
  }

  let message = `The service answered ${response.status}`;
  let problems: Record<string, string[]> = {};
  let alreadyRegistered = false;
  try {
    const failure = (await response.json()) as {
      error?: string;
      problems?: Record<string, string[]>;
      already_registered?: boolean;
    };
    if (failure.error) {
      message = failure.error.charAt(0).toUpperCase() + failure.error.slice(1);
    }
    if (failure.problems) {
      problems = failure.problems;
    }
    alreadyRegistered = failure.already_registered === true;
  } catch {
    // A refusal with no JSON body — a proxy answering instead of the service.
  }
  throw new ClaimRefused(response.status, message, problems, alreadyRegistered);
}
